import {
  adminMockModeEnabledForEnvironment,
  developmentControlsVisibleForEnvironment,
  normalizeBooplyEnvironment,
  validateProductionSafety,
  type BooplyEnvironment,
  type ProductionSafetyIssue
} from "../../../../packages/config/src/environment";

export const WEB_API_BASE_ENV_KEY = "VITE_BOOPLY_API_BASE_URL";
export const WEB_RELAY_URL_ENV_KEY = "VITE_BOOPLY_RELAY_URL";
export const WEB_ENVIRONMENT_ENV_KEY = "VITE_BOOPLY_ENVIRONMENT";
export const WEB_ADMIN_MOCK_MODE_ENV_KEY = "VITE_BOOPLY_ADMIN_MOCK_MODE";

export const DEFAULT_LOCAL_API_BASE_URL = "http://localhost:8787/v1";
export const DEFAULT_LOCAL_RELAY_URL = "ws://localhost:8788/v1";

export type WebEnvironmentSource = Record<string, string | boolean | undefined>;

export interface WebEnvironmentConfig {
  readonly environment: BooplyEnvironment;
  readonly apiBaseUrl: string;
  readonly relayUrl: string;
  readonly adminMockModeEnabled: boolean;
  readonly developmentControlsVisible: boolean;
  readonly productionSafetyIssues: readonly ProductionSafetyIssue[];
}

export function getWebEnvironmentConfig(source = currentImportMetaEnv()): WebEnvironmentConfig {
  const environment = getWebEnvironment(source);
  const productionFlag = source?.PROD === true || environment === "production";
  const apiBaseUrl = readString(source, WEB_API_BASE_ENV_KEY) ?? (productionFlag ? "" : DEFAULT_LOCAL_API_BASE_URL);
  const relayUrl = readString(source, WEB_RELAY_URL_ENV_KEY) ?? (productionFlag ? "" : DEFAULT_LOCAL_RELAY_URL);
  const adminMockModeEnabled = isWebAdminMockMode(source);
  const developmentControlsVisible = isWebDevelopmentControlVisible(source);

  return {
    adminMockModeEnabled,
    apiBaseUrl,
    developmentControlsVisible,
    environment,
    productionSafetyIssues: validateProductionSafety({
      adminMockModeEnabled,
      apiBaseUrl,
      environment,
      mockDataEnabled: adminMockModeEnabled,
      relayUrl
    }),
    relayUrl
  };
}

export function getWebApiBaseUrl(source = currentImportMetaEnv()): string {
  const config = getWebEnvironmentConfig(source);
  assertNoProductionSafetyIssues(config.productionSafetyIssues);
  return config.apiBaseUrl;
}

export function getWebRelayUrl(source = currentImportMetaEnv()): string {
  const config = getWebEnvironmentConfig(source);
  assertNoProductionSafetyIssues(config.productionSafetyIssues);
  return config.relayUrl;
}

export function isWebAdminMockMode(source = currentImportMetaEnv()): boolean {
  const environment = getWebEnvironment(source);
  const productionFlag = source?.PROD === true || environment === "production";

  return adminMockModeEnabledForEnvironment(
    environment,
    source?.[WEB_ADMIN_MOCK_MODE_ENV_KEY],
    productionFlag
  );
}

export function isWebDevelopmentControlVisible(source = currentImportMetaEnv()): boolean {
  const environment = getWebEnvironment(source);
  return developmentControlsVisibleForEnvironment(environment, source?.PROD === true);
}

export function getWebEnvironment(source = currentImportMetaEnv()): BooplyEnvironment {
  const configured = readString(source, WEB_ENVIRONMENT_ENV_KEY) ?? readString(source, "MODE");
  const fallback = source?.PROD === true ? "production" : "local";

  return normalizeBooplyEnvironment(configured, fallback);
}

function assertNoProductionSafetyIssues(issues: readonly ProductionSafetyIssue[]): void {
  if (issues.length === 0) {
    return;
  }

  throw new Error(`Unsafe production environment: ${issues.map((issue) => issue.message).join(" ")}`);
}

function readString(source: WebEnvironmentSource | undefined, key: string): string | undefined {
  const value = source?.[key];

  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

function currentImportMetaEnv(): WebEnvironmentSource | undefined {
  return (import.meta as ImportMeta & {
    readonly env?: WebEnvironmentSource;
  }).env;
}
